/** Grupo escandallo IMPORT (DDP). */
export const IMPORT_GROUP_ID = 13;

/** Incoterm DDP en TON. */
export const DDP_INCOTERM_ID = 4;

/** Artículo genérico A3 usado en POs de freight/import. */
export const IMPORT_A3_CODART = "0";

export function parseImportAmount(val) {
  const n = Number(String(val ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export function isDdpIncoterm(idIncoterm) {
  return Number(idIncoterm) === DDP_INCOTERM_ID;
}

export function isImportEscandalloLine(line = {}) {
  return (
    Number(line.ESCANDALLO_GRUPOS ?? line.escandalloGrupos) === IMPORT_GROUP_ID
  );
}

export function getImportLineAmount(line = {}) {
  const converted = parseImportAmount(
    line.CosteConvertido ?? line.costeConvertido,
  );
  if (converted > 0) return converted;
  return parseImportAmount(line.Coste ?? line.coste);
}

export function getImportLineProveedorKey(line = {}) {
  const raw =
    line.PROVEEDOR_ESCANDALLO ??
    line.Proveedor ??
    line.proveedor ??
    line.CodPro ??
    "";
  const text = String(raw ?? "").trim();
  return text || "__none__";
}

/**
 * Nombre del proveedor TON (escandallo). CODPROV TON ≠ CODPRO A3.
 */
export function resolveMatrixProveedorNombre(codprov, matrixProveedores = []) {
  const code = String(codprov || "").trim();
  if (!code || code === "__none__") return "";
  const match = (matrixProveedores || []).find(
    (p) => String(p.CODPROV ?? p.codprov ?? "").trim() === code,
  );
  return String(match?.N_PROVEEDOR ?? match?.n_proveedor ?? "").trim();
}

function normalizeProvName(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

/**
 * Empareja proveedor TON → CODPRO A3 por nombre (no por código).
 * Los catálogos no comparten numeración (p.ej. TON 440 = MARS EXPRESS,
 * A3 440 = SHAOXING). La API A3 expone el nombre en `NOMBRE`.
 */
export function matchA3CodproByProveedorNombre(
  matrixNombre = "",
  a3Proveedores = [],
  matrixCod = "",
) {
  const name = normalizeProvName(matrixNombre);
  const list = Array.isArray(a3Proveedores) ? a3Proveedores : [];
  const a3Name = (p) =>
    normalizeProvName(
      p?.NOMBRE ?? p?.nombre ?? p?.NOMPRO ?? p?.nompro ?? p?.RAZON ?? p?.razon,
    );
  if (name) {
    const exact = list.find((p) => {
      const n = a3Name(p);
      if (!n) return false;
      return (
        n === name ||
        n.includes(name) ||
        (name.length >= 4 && name.includes(n) && n.length >= 4)
      );
    });
    if (exact) {
      return String(exact.CODPRO ?? exact.codpro ?? "").trim();
    }
    const tokens = name.split(/[^A-Z0-9]+/).filter((t) => t.length >= 4);
    if (tokens.length) {
      const byToken = list.find((p) => {
        const n = a3Name(p);
        return Boolean(n) && tokens.every((t) => n.includes(t));
      });
      if (byToken) {
        return String(byToken.CODPRO ?? byToken.codpro ?? "").trim();
      }
    }
  }
  // Solo usar el código TON si el mismo CODPRO existe en A3 y el nombre se parece.
  const code = String(matrixCod || "").trim();
  if (code && name) {
    const byCode = list.find(
      (p) => String(p.CODPRO ?? p.codpro ?? "").trim() === code,
    );
    if (byCode) {
      const n = a3Name(byCode);
      const tokens = name.split(/[^A-Z0-9]+/).filter((t) => t.length >= 3);
      if (n && tokens.some((t) => n.includes(t))) {
        return code;
      }
    }
  }
  return "";
}

export function resolveImportLineConcepto(line = {}, subgrupos = []) {
  const fromLine = String(
    line.SUBGRUPO ?? line.Subgrupo ?? line.concepto ?? "",
  ).trim();
  if (fromLine) return fromLine;

  const subId = Number(line.ESCANDALLO_SUBGRUPO ?? line.escandalloSubgrupo);
  if (!Number.isFinite(subId) || subId <= 0) return "Import";

  const lists = [
    ...(Array.isArray(line.subgruposList) ? line.subgruposList : []),
    ...(Array.isArray(subgrupos) ? subgrupos : []),
  ];
  const match = lists.find(
    (s) => Number(s.ID_SUBGRUPO ?? s.idSubgrupo ?? s.id) === subId,
  );
  const name = String(
    match?.SUBGRUPO ?? match?.subgrupo ?? match?.nombre ?? "",
  ).trim();
  return name || `Subgrupo ${subId}`;
}

/** Pedidos CODART 0 (freight/import) no cuentan como prendas pedidas. */
export function isImportA3PedidoRow(row = {}) {
  const codart = String(row.CodArt ?? row.codart ?? "").trim();
  if (codart === IMPORT_A3_CODART) return true;
  const ref = String(row.Referencia ?? row.referencia ?? "")
    .trim()
    .toUpperCase();
  return /-IMP(?:-\d+)?$/.test(ref);
}

/**
 * ¿Hay líneas IMPORT con coste > 0?
 */
export function hasImportCostLines(lines = []) {
  return (lines || []).some(
    (line) => isImportEscandalloLine(line) && getImportLineAmount(line) > 0,
  );
}

/**
 * ¿Debemos ofrecer el PO de import tras crear el de prenda?
 */
export function shouldOfferImportA3Pedido({
  idIncoterm,
  lines = [],
  a3PedidosRows = [],
  referenciaImp = "",
} = {}) {
  if (hasExistingImportA3Pedido(a3PedidosRows, referenciaImp)) return false;
  return isDdpIncoterm(idIncoterm) || hasImportCostLines(lines);
}

/**
 * Ya existe un pedido compra-only CODART 0 (opcionalmente misma ref -IMP).
 */
export function hasExistingImportA3Pedido(
  a3PedidosRows = [],
  referenciaImp = "",
) {
  const refWanted = String(referenciaImp || "")
    .trim()
    .toLowerCase();
  return (a3PedidosRows || []).some((row) => {
    const codart = String(row.CodArt ?? row.codart ?? "").trim();
    if (codart !== IMPORT_A3_CODART) return false;
    const hasCompra = Boolean(row.IdPedc ?? row.idPedc);
    const hasVenta = Boolean(row.IdPedv ?? row.idPedv);
    if (!hasCompra || hasVenta) return false;
    if (!refWanted) return true;
    const refRow = String(
      row.Referencia ?? row.referencia ?? row.SerieCompra ?? "",
    )
      .trim()
      .toLowerCase();
    const refDoc = String(row.ReferenciaCompra ?? row.referenciaCompra ?? "")
      .trim()
      .toLowerCase();
    return (
      refRow === refWanted ||
      refDoc === refWanted ||
      refRow.endsWith("-imp") ||
      String(row.Referencia ?? "")
        .toUpperCase()
        .includes("-IMP")
    );
  });
}

export function buildImportReferencia(referenciaPrenda = "") {
  const base = String(referenciaPrenda || "")
    .trim()
    .replace(/-IMP$/i, "");
  if (!base) return "IMP";
  return `${base}-IMP`;
}

/**
 * Agrupa líneas IMPORT por proveedor y construye lineasConcepto A3.
 * Nota: `codproEscandallo` es CODPROV TON; no usarlo como CODPRO A3.
 * @returns {{ groups: Array<{ proveedorKey, codproEscandallo, proveedorNombre, codpro, lineasConcepto, total }> }}
 */
export function buildImportA3PedidoGroups(
  lines = [],
  subgrupos = [],
  matrixProveedores = [],
) {
  const byProv = new Map();

  for (const line of lines || []) {
    if (!isImportEscandalloLine(line)) continue;
    const precio = getImportLineAmount(line);
    if (!(precio > 0)) continue;

    const proveedorKey = getImportLineProveedorKey(line);
    const desclin = resolveImportLineConcepto(line, subgrupos).slice(0, 100);
    const entry = byProv.get(proveedorKey) || {
      proveedorKey,
      // Código TON (escandallo) — distinto del CODPRO A3.
      codproEscandallo: proveedorKey === "__none__" ? "" : proveedorKey,
      proveedorNombre: resolveMatrixProveedorNombre(
        proveedorKey,
        matrixProveedores,
      ),
      // No prellenar CODPRO A3 con el código TON.
      codpro: "",
      lineasConcepto: [],
      total: 0,
    };
    if (!entry.proveedorNombre) {
      entry.proveedorNombre = resolveMatrixProveedorNombre(
        proveedorKey,
        matrixProveedores,
      );
    }
    entry.lineasConcepto.push({
      desclin,
      unidades: 1,
      precio: Number(precio.toFixed(6)),
      subgrupoId:
        Number(line.ESCANDALLO_SUBGRUPO ?? line.escandalloSubgrupo) || null,
    });
    entry.total = Number((entry.total + precio).toFixed(6));
    byProv.set(proveedorKey, entry);
  }

  return {
    groups: [...byProv.values()].sort((a, b) =>
      String(a.proveedorNombre || a.codproEscandallo).localeCompare(
        String(b.proveedorNombre || b.codproEscandallo),
      ),
    ),
  };
}

/**
 * Payload createA3Pedidos para un grupo import.
 */
export function buildImportA3PedidoPayload({
  group,
  referencia,
  dbSource,
  idPresupuesto,
  idProyecto,
  fecha,
  centroCosteEmpresa,
  centroCosteMarca,
  centroCosteDepartamento,
  tipiva,
} = {}) {
  const lineasConcepto = (group?.lineasConcepto || []).map((l) => ({
    desclin: String(l.desclin || "Import").slice(0, 100),
    unidades: Number(l.unidades) > 0 ? Number(l.unidades) : 1,
    precio: Number(l.precio) >= 0 ? Number(l.precio) : 0,
    ...(tipiva != null ? { tipiva } : {}),
  }));

  const total = lineasConcepto.reduce(
    (acc, l) => acc + Number(l.unidades) * Number(l.precio),
    0,
  );

  return {
    modo: "compra",
    codart: IMPORT_A3_CODART,
    codpro: String(group?.codpro || "").trim(),
    referencia: String(referencia || "").trim(),
    unidadesCompra: 1,
    precioCoste: Number(total.toFixed(6)),
    lineasConcepto,
    dbSource: String(dbSource || "STAGE_DESIGN").trim(),
    idPresupuesto: idPresupuesto ?? null,
    idProyecto: idProyecto ?? null,
    fecha: fecha || null,
    centroCosteEmpresa: centroCosteEmpresa || "",
    centroCosteMarca: centroCosteMarca || "",
    centroCosteDepartamento: centroCosteDepartamento || "",
  };
}
